/**
 * useSpotFuturesPrices Hook
 *
 * Polls the /standby/stats endpoint to get price information for opening new positions.
 * Client handles all calculations (differentials, conversions).
 *
 * ## Conversion Rates
 *
 * Rates come from usePriceUpdates() hook (see priceWebSocketManager.ts).
 * The USDT/KRW rate is already displayed in HeaderStats - no need to show it again.
 *
 * Formula: usdtKrw = upbitBtcKrw / binanceBtcUsdt
 *
 * ## Quote Currency Conversion
 *
 * - USDT quote: no conversion (already in USDT)
 * - KRW quote: divide by usdtKrw rate
 * - BTC quote: multiply by binanceBtcUsdt
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useApiConfig } from './useApiConfig';
import { Exchange } from '../types/futuresSpot';

// ============================================================================
// Types matching server response
// ============================================================================

// Exchange enum values: Upbit=0, Bithumb=1, Coinone=2, Binance=3, Htx=4, Gateio=5, Kucoin=6, Okx=7, Mexc=8, Bybit=9, Bitget=10
const EXCHANGE_MAP: Record<number, Exchange> = {
  0: Exchange.Upbit,
  1: Exchange.Bithumb,
  3: Exchange.Binance,
};

/**
 * Conversion rates for quote currencies to USDT
 * From HeaderPrices (usePriceUpdates hook)
 */
export interface QuoteToUsdtRates {
  usdtKrw: number | null;   // USDT/KRW rate (e.g., 1400) - divide KRW price by this
  btcUsdt: number | null;   // BTC/USDT rate (e.g., 100000) - multiply BTC price by this
}

/**
 * Spot exchange data from server
 */
export interface SpotInfo {
  exchange: number;   // Exchange as u8
  quote: string;      // "USDT" or "KRW" or "BTC"
  ask_price: number;  // Best ask price (for buying)
}

/**
 * Standby opportunity for a base asset
 */
export interface StandbyOpportunity {
  base: string;                // "BTC"
  f_bid_price: number;         // Futures best bid (for shorting)
  funding: number | null;      // Funding rate
  funding_time: number | null; // Next funding time (ms)
  funding_interval_hours: number | null; // Rounded funding interval in hours
  spot: SpotInfo[];
}

/**
 * Full API response
 */
export interface StandbyStatsResponse {
  status: string;
  timestamp: number;
  opportunities: StandbyOpportunity[];
}

// ============================================================================
// Converted Types for UI Components
// ============================================================================

export interface PriceLevel {
  price: number;
  volume: number;
}

export interface OrderbookData {
  timestamp: number;
  bids: Array<[number, number]>;
  asks: Array<[number, number]>;
  bestBid: PriceLevel | null;
  bestAsk: PriceLevel | null;
  ccxtSymbol: string;
}

export interface ConvertedSpotExchangeData {
  exchange: Exchange;
  quote: 'USDT' | 'KRW' | 'BTC';
  lastPrice: number | null;
  bestAsk: PriceLevel | null;
  differentialPercent: number | null;
}

export interface ConvertedPositionOpportunity {
  baseAsset: string;
  futuresSymbol: string;
  futuresCcxtSymbol: string;
  futuresBestBid: PriceLevel | null;
  fundingRate: number | null;
  nextFundingTime: number | null;
  fundingIntervalHours: number | null;
  spotData: ConvertedSpotExchangeData[];
  bestDifferentialPercent: number | null;
  bestDifferentialExchange: Exchange | null;
  timestamp: number;
}

// ============================================================================
// Conversion utilities
// ============================================================================

function toExchangeEnum(exchangeNum: number): Exchange | null {
  return EXCHANGE_MAP[exchangeNum] ?? null;
}

/**
 * Calculate differential percentage
 * Formula: (spot_ask - futures_bid) / spot_ask * 100
 *
 * Conversion logic:
 * - USDT quote: no conversion needed (already in USDT)
 * - KRW quote: divide by USDT/KRW rate (e.g., 1400000 KRW / 1400 = 1000 USDT)
 * - BTC quote: multiply by BTC/USDT rate (e.g., 0.01 BTC * 100000 = 1000 USDT)
 */
function calculateDifferential(
  futuresBid: number,
  spotAsk: number,
  quote: string,
  rates: QuoteToUsdtRates
): number | null {
  if (futuresBid <= 0 || spotAsk <= 0) return null;

  let spotAskInUsdt = spotAsk;

  if (quote === 'KRW') {
    if (!rates.usdtKrw || rates.usdtKrw <= 0) return null;
    spotAskInUsdt = spotAsk / rates.usdtKrw;
  } else if (quote === 'BTC') {
    if (!rates.btcUsdt || rates.btcUsdt <= 0) return null;
    spotAskInUsdt = spotAsk * rates.btcUsdt;
  }
  // USDT quote: spotAskInUsdt = spotAsk (no conversion)

  if (spotAskInUsdt <= 0) return null;
  return ((spotAskInUsdt - futuresBid) / spotAskInUsdt) * 100;
}

/**
 * Convert server response to UI format
 */
function convertOpportunity(
  opp: StandbyOpportunity,
  rates: QuoteToUsdtRates
): ConvertedPositionOpportunity {
  const now = Date.now();

  let bestDiff: number | null = null;
  let bestExchange: Exchange | null = null;

  const spotData: ConvertedSpotExchangeData[] = opp.spot
    .map((spot) => {
      const exchange = toExchangeEnum(spot.exchange);
      if (!exchange) return null;

      const bestAsk: PriceLevel | null = spot.ask_price > 0
        ? { price: spot.ask_price, volume: 0 }
        : null;

      const diffPct = calculateDifferential(
        opp.f_bid_price,
        spot.ask_price,
        spot.quote,
        rates
      );

      // Track best differential (most negative = most favorable entry)
      if (diffPct !== null && (bestDiff === null || diffPct < bestDiff)) {
        bestDiff = diffPct;
        bestExchange = exchange;
      }

      return {
        exchange,
        quote: spot.quote as 'USDT' | 'KRW' | 'BTC',
        lastPrice: spot.ask_price > 0 ? spot.ask_price : null,
        bestAsk,
        differentialPercent: diffPct,
      };
    })
    .filter((s): s is ConvertedSpotExchangeData => s !== null);

  const futuresBestBid: PriceLevel | null = opp.f_bid_price > 0
    ? { price: opp.f_bid_price, volume: 0 }
    : null;

  return {
    baseAsset: opp.base,
    futuresSymbol: `${opp.base}USDT`,
    futuresCcxtSymbol: `${opp.base}/USDT:USDT`,
    futuresBestBid,
    fundingRate: opp.funding,
    nextFundingTime: opp.funding_time,
    fundingIntervalHours: opp.funding_interval_hours,
    spotData,
    bestDifferentialPercent: bestDiff,
    bestDifferentialExchange: bestExchange,
    timestamp: now,
  };
}

// ============================================================================
// Hook Result Type
// ============================================================================

export interface UseSpotFuturesPricesResult {
  opportunities: ConvertedPositionOpportunity[];
  rawResponse: StandbyStatsResponse | null;
  loading: boolean;
  error: string | null;
  lastUpdate: number;
  refetch: () => Promise<void>;
}

// ============================================================================
// Hook Implementation
// ============================================================================

const POLLING_INTERVAL_MS = 3000;

/**
 * Hook for polling standby stats endpoint
 *
 * @param rates - Conversion rates from usePriceUpdates (HeaderPrices)
 *   - usdtKrw: USDT/KRW rate (e.g., upbitUsdtKrw = 1400)
 *   - btcUsdt: BTC/USDT rate (e.g., binanceBtcUsdt = 100000)
 * @param enabled - Whether polling is enabled
 * @param pollingInterval - Polling interval in ms (default 2000)
 */
export function useSpotFuturesPrices(
  rates: QuoteToUsdtRates,
  enabled = true,
  pollingInterval = POLLING_INTERVAL_MS
): UseSpotFuturesPricesResult {
  const { apiUrl } = useApiConfig();

  const [opportunities, setOpportunities] = useState<ConvertedPositionOpportunity[]>([]);
  const [rawResponse, setRawResponse] = useState<StandbyStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState(0);

  const abortControllerRef = useRef<AbortController | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const ratesRef = useRef<QuoteToUsdtRates>(rates);

  // Keep ref in sync with rates
  useEffect(() => {
    ratesRef.current = rates;
  }, [rates]);

  const fetchOpportunities = useCallback(async () => {
    if (!apiUrl) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const url = `${apiUrl}/standby/stats`;

      const response = await fetch(url, {
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: StandbyStatsResponse = await response.json();

      if (data.status !== 'ok') {
        throw new Error(`API error: ${data.status}`);
      }

      // Convert opportunities using current rates
      const converted = data.opportunities.map(opp =>
        convertOpportunity(opp, ratesRef.current)
      );

      setOpportunities(converted);
      setRawResponse(data);
      setLastUpdate(Date.now());
      setError(null);
      setLoading(false);

    } catch (err) {
      if ((err as Error).name === 'AbortError') return;

      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[useSpotFuturesPrices] Fetch error:', errorMessage);
      setError(errorMessage);
      setLoading(false);
    }
  }, [apiUrl]);

  const refetch = useCallback(async () => {
    await fetchOpportunities();
  }, [fetchOpportunities]);

  useEffect(() => {
    if (!enabled || !apiUrl) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    fetchOpportunities();

    pollingRef.current = setInterval(() => {
      fetchOpportunities();
    }, pollingInterval);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [enabled, apiUrl, pollingInterval, fetchOpportunities]);

  return {
    opportunities,
    rawResponse,
    loading,
    error,
    lastUpdate,
    refetch,
  };
}
